import { useEffect, useState } from "react";
import { Save } from "lucide-react";

import { PLATFORMS } from "../../lib/platforms";
import { clean } from "../../lib/text";
import { Field, GhostBtn, INPUT, PrimaryBtn, Sheet, DatePicker } from "../ui";
import PostCard from "./PostCard";

const MEDIA_LABEL = {
  tiktok: "Video script",
  youtube: "Shooting script",
  instagram: "Carousel or visual idea",
};

export default function PostEditor({ open, post, onClose, onSave }) {
  const [draft, setDraft] = useState(post);

  useEffect(() => {
    setDraft(post);
  }, [post]);

  if (!open || !draft) return null;

  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = () => {
    onSave({
      ...draft,
      title: clean(draft.title),
      body: clean(draft.body),
      hook: clean(draft.hook),
      format: clean(draft.format),
      caption_direction: clean(draft.caption_direction),
      cta: clean(draft.cta),
      media_idea: clean(draft.media_idea),
      feature: clean(draft.feature),
      hashtags: (draft.hashtags || []).map((h) => clean(h)),
    });
  };

  const xLen =
    draft.platform === "x"
      ? (draft.body + " " + (draft.hashtags || []).map((h) => "#" + h).join(" ")).trim().length
      : null;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      wide
      title={`Edit ${PLATFORMS[draft.platform].name} post`}
      footer={
        <>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={save}>
            <Save size={16} />
            Save changes
          </PrimaryBtn>
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-4">
          <Field label="Content idea">
            <input className={INPUT} value={draft.title || ""} onChange={(e) => upd("title", e.target.value)} />
          </Field>

          <Field label="Format" hint="Static post, Carousel, Reel, Skit, Talking head, etc.">
            <input className={INPUT} value={draft.format || ""} onChange={(e) => upd("format", e.target.value)} />
          </Field>

          <Field label="Hook">
            <textarea
              className={`${INPUT} min-h-[70px] leading-relaxed`}
              value={draft.hook || ""}
              onChange={(e) => upd("hook", e.target.value)}
            />
          </Field>

          <Field label="Caption" hint={xLen !== null ? `${xLen}/280` : ""}>
            <textarea
              className={`${INPUT} min-h-[160px] leading-relaxed`}
              value={draft.body || ""}
              onChange={(e) => upd("body", e.target.value)}
            />
          </Field>

          <Field label="Caption direction" hint="guidance for the caption, not the caption itself">
            <textarea
              className={`${INPUT} min-h-[80px] leading-relaxed`}
              value={draft.caption_direction || ""}
              onChange={(e) => upd("caption_direction", e.target.value)}
            />
          </Field>

          <Field label="Hashtags" hint="space or comma separated, no #">
            <input
              className={INPUT}
              value={(draft.hashtags || []).join(" ")}
              onChange={(e) =>
                upd(
                  "hashtags",
                  e.target.value
                    .split(/[\s,]+/)
                    .map((s) => s.replace(/^#/, ""))
                    .filter(Boolean),
                )
              }
            />
          </Field>

          <Field label="Call to action">
            <input className={INPUT} value={draft.cta || ""} onChange={(e) => upd("cta", e.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <DatePicker value={draft.date || ""} onChange={(v) => upd("date", v)} />
            </Field>
            <Field label="Time">
              <input type="time" className={INPUT} value={draft.time || ""} onChange={(e) => upd("time", e.target.value)} />
            </Field>
          </div>

          <Field label="Feature or topic">
            <input className={INPUT} value={draft.feature || ""} onChange={(e) => upd("feature", e.target.value)} />
          </Field>

          <Field label={MEDIA_LABEL[draft.platform] || "Visual idea"}>
            <textarea
              className={`${INPUT} min-h-[90px] leading-relaxed`}
              value={draft.media_idea || ""}
              onChange={(e) => upd("media_idea", e.target.value)}
            />
          </Field>
        </div>

        <div>
          <div className="text-xs font-medium text-faint mb-2 uppercase tracking-wide">Live preview</div>
          <PostCard post={draft} />
        </div>
      </div>
    </Sheet>
  );
}